import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HighlightIcon from '@mui/icons-material/Highlight';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import TitleIcon from '@mui/icons-material/Title';
import {
  FormControl,
  InputLabel,
  FormHelperText,
  Toolbar,
  IconButton,
  Typography,
  Tooltip,
  Divider,
  ButtonGroup,
  useTheme,
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import Color from '@tiptap/extension-color';
import HardBreak from '@tiptap/extension-hard-break';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Placeholder from '@tiptap/extension-placeholder';
import Strike from '@tiptap/extension-strike';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import {useEditor, EditorContent, JSONContent, Editor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {DlMuiPopper, MaximizeButton, TranslateUtils, useMuiAnchor} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isEqual} from 'lodash';
import React, {useMemo, useState, useEffect, useRef, useCallback, FocusEvent, useLayoutEffect} from 'react';
import {useIntl} from 'react-intl';

import lightFilterNodeStyles from './LightFilterNodeStyles.less';
import {Formatters, useFormatters} from './LightFormatter';

export const TextAlignWithLists = TextAlign.extend({
  addGlobalAttributes() {
    const original = TextAlign.config.addGlobalAttributes?.call(this) ?? [];

    return [
      ...original,
      {
        types: ['bulletList', 'orderedList'],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => element.style.textAlign || element.getAttribute('data-align') || null,
            renderHTML: (attributes) => {
              if (!attributes.textAlign) return {};
              return {
                'style': `text-align: ${attributes.textAlign}`,
                'data-align': attributes.textAlign,
              };
            },
          },
        },
      },
    ];
  },
});

// ============================================================================
// TYPES & ENUMS
// ============================================================================

enum TEXT_COLORS {
  YELLOW = '#D2A106',
  RED = '#FF8389',
  PURPLE = '#BE95FF',
  CYAN = '#33B1FF',
  GREEN = '#42BE65',
}

enum HIGHLIGHT_COLORS {
  YELLOW = '#FDDC69',
  RED = '#FFD7D9',
  PURPLE = '#E8DAFF',
  CYAN = '#BAE6FF',
  GREEN = '#A7F0BA',
}

type ToolbarItemType = 'button' | 'dropdown' | 'menu' | 'divider';
type AlignmentType = 'left' | 'center' | 'right' | 'justify';

interface BaseToolbarItem {
  key: string;
  type: ToolbarItemType;
  visible?: boolean;
  disabled?: boolean;
}

interface ButtonToolbarItem extends BaseToolbarItem {
  type: 'button';
  icon: React.ReactNode;
  title: string;
  action: (editor: Editor) => void;
  isActive?: (editor: Editor) => boolean;
}

interface DropdownToolbarItem extends BaseToolbarItem {
  type: 'dropdown';
  icon: React.ReactNode;
  title: string;
  getCurrentValue?: (editor: Editor) => string;
  options: Array<{
    key: string;
    label: string;
    value: any;
    action: (editor: Editor) => void;
    isActive?: (editor: Editor) => boolean;
    icon?: React.ReactNode;
    style?: React.CSSProperties;
  }>;
}

interface MenuToolbarItem extends BaseToolbarItem {
  type: 'menu';
  icon: React.ReactNode;
  title: string;
  menuItems: Array<{
    key: string;
    label: string;
    value: any;
    action?: (editor: Editor) => void;
    isActive?: (editor: Editor) => boolean;
    icon?: React.ReactNode;
    divider?: boolean;
  }>;
}

interface DividerToolbarItem extends BaseToolbarItem {
  type: 'divider';
}

type ToolbarItem = ButtonToolbarItem | DropdownToolbarItem | MenuToolbarItem | DividerToolbarItem;

interface ToolbarGroup {
  items: ToolbarItem[];
}

interface ColorMenuItemProps {
  colorValue: string;
  isSelected: boolean;
  onClick: () => void;
}

interface LightRichTextAreaFilterSlots {
  Root?: React.ElementType;
  FormControl?: React.ElementType;
  InputLabel?: React.ElementType;
  Toolbar?: React.ElementType;
  IconButton?: React.ElementType;
  Content?: React.ElementType;
  HelperText?: React.ElementType;
}

interface LightRichTextAreaFilterSlotProps {
  Root?: any;
  FormControl?: any;
  InputLabel?: any;
  Toolbar?: any;
  IconButton?: any;
  Content?: any;
  HelperText?: any;
}

interface LightRichTextAreaFilterProps {
  config?: {
    name: string;
    ui: {
      label?: string;
      placeholder?: string;
    };
  };
  isDefaultValue?: boolean;
  value?: JSONContent;
  onChange?: (json: JSONContent, html: string, text: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
  debounceDelay?: number;
  slots?: LightRichTextAreaFilterSlots;
  slotProps?: LightRichTextAreaFilterSlotProps;
  toolbarGroups?: ToolbarGroup[];
  contentStyles?: React.CSSProperties;
  variant?: 'outlined' | 'filled' | 'standard';
  fullWidth?: boolean;
  optional?: boolean;
  canMaximize?: boolean;
  normalTextColor?: string;
  onGetCurrentValue?: (getValueFn: () => {json: JSONContent; html: string; text: string}) => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const useDebounce = (callback: ((json: JSONContent, html: string, text: string) => void) | undefined, delay: number) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback(
    (json: JSONContent, html: string, text: string) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (!callbackRef.current) return;

      timeoutRef.current = setTimeout(() => {
        if (callbackRef?.current) {
          callbackRef?.current(json, html, text);
        }
      }, delay);
    },
    [delay]
  );
};

const jsonContentEquals = (a: JSONContent | undefined, b: JSONContent | undefined): boolean => isEqual(a, b);

const isContentEmpty = (json: JSONContent): boolean => {
  if (!json || !json.content || json.content.length === 0) return true;

  if (json.content.length === 1 && json.content[0].type === 'paragraph') {
    const paragraph = json.content[0];
    return (
      !paragraph.content ||
      paragraph.content.length === 0 ||
      (paragraph.content.length === 1 &&
        paragraph.content[0].type === 'text' &&
        (!paragraph.content[0].text || paragraph.content[0].text.trim() === ''))
    );
  }

  return false;
};

const getAlignmentIcon = (alignment: AlignmentType) => {
  switch (alignment) {
    case 'center':
      return <FormatAlignCenterIcon fontSize='small' />;
    case 'right':
      return <FormatAlignRightIcon fontSize='small' />;
    case 'justify':
      return <FormatAlignJustifyIcon fontSize='small' />;
    default:
      return <FormatAlignLeftIcon fontSize='small' />;
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const ColorMenuItem: React.FC<ColorMenuItemProps> = ({colorValue, isSelected, onClick}) => {
  return (
    <MenuItem
      onClick={onClick}
      selected={isSelected}
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'py': 1,
        'minHeight': 40,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <Box
        component='span'
        sx={{
          width: 20,
          height: 20,
          backgroundColor: colorValue,
          borderRadius: '50%',
          border: '2px solid',
          borderColor: 'background.paper',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
          flexShrink: 0,
        }}
      />
    </MenuItem>
  );
};

interface ToolbarButtonProps {
  item: ButtonToolbarItem;
  editor: Editor;
  disabled: boolean;
  slotProps: any;
  safeExecuteAction: (action: (editor: Editor) => void) => void;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({item, editor, disabled, slotProps, safeExecuteAction}) => {
  const theme = useTheme();
  const isActive = item.isActive ? item.isActive(editor) : false;
  const isDisabled = item.disabled || disabled || !editor?.can().chain().focus().run();

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(true);

  return (
    <div onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave}>
      <IconButton
        onClick={() => safeExecuteAction(item.action)}
        size='small'
        disabled={isDisabled}
        aria-label={item.title}
        aria-pressed={isActive}
        {...slotProps.IconButton}
        style={{
          'backgroundColor': isActive ? theme.palette.action.selected : 'transparent',
          '&:hover': {
            backgroundColor: isActive ? theme.palette.action.selected : theme.palette.action.hover,
          },
          ...slotProps.IconButton?.style,
        }}
      >
        {item.icon}
      </IconButton>
      <DlMuiPopper hasTooltip anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{item.title}</Typography>
      </DlMuiPopper>
    </div>
  );
};

interface ToolbarDropdownProps {
  item: DropdownToolbarItem;
  editor: Editor;
  disabled: boolean;
  anchorEl: HTMLElement | null;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  safeExecuteAction: (action: (editor: Editor) => void) => void;
}

const ToolbarDropdown: React.FC<ToolbarDropdownProps> = ({item, editor, disabled, anchorEl, onOpen, onClose, safeExecuteAction}) => {
  const theme = useTheme();
  const currentValue = item.getCurrentValue ? item.getCurrentValue(editor) : '';

  const {mouseEventEnter, mouseEventLeave, open: tooltipOpen, anchorEl: tooltipAnchorEl} = useMuiAnchor(true);

  return (
    <>
      <div onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave}>
        <Button
          size='small'
          variant='outlined'
          onClick={onOpen}
          endIcon={<ArrowDropDownIcon />}
          disabled={disabled}
          sx={{
            'minWidth': 'auto',
            'textTransform': 'none',
            'fontSize': '0.75rem',
            'padding': '4px 8px',
            'borderColor': theme.palette.divider,
            'color': theme.palette.text.primary,
            '&:hover': {
              borderColor: theme.palette.text.primary,
            },
          }}
        >
          {currentValue || item.title}
        </Button>
        <DlMuiPopper hasTooltip anchorEl={tooltipAnchorEl} open={tooltipOpen}>
          <Typography variant={'popper'}>{item.title}</Typography>
        </DlMuiPopper>
      </div>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
      >
        {item.options.map((option) => (
          <MenuItem
            key={option.key}
            selected={option.isActive ? option.isActive(editor) : false}
            onClick={() => {
              safeExecuteAction(option.action);
              onClose();
            }}
            sx={option.style}
          >
            {option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface ToolbarMenuProps {
  item: MenuToolbarItem;
  editor: Editor;
  disabled: boolean;
  anchorEl: HTMLElement | null;
  onOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onClose: () => void;
  safeExecuteAction: (action?: (editor: Editor) => void) => void;
  normalTextColor?: string;
}

const ToolbarMenu: React.FC<ToolbarMenuProps> = ({
  item,
  editor,
  disabled,
  anchorEl,
  onOpen,
  onClose,
  safeExecuteAction,
  normalTextColor,
}) => {
  const isActive = item.menuItems.some((menuItem) => (menuItem.isActive ? menuItem.isActive(editor) : false));

  const {mouseEventEnter, mouseEventLeave, open: tooltipOpen, anchorEl: tooltipAnchorEl} = useMuiAnchor(true);

  return (
    <>
      <div onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave}>
        <IconButton onClick={onOpen} size='small' disabled={disabled} aria-label={item.title} aria-pressed={isActive}>
          {item.icon}
        </IconButton>
        <DlMuiPopper hasTooltip anchorEl={tooltipAnchorEl} open={tooltipOpen}>
          <Typography variant={'popper'}>{item.title}</Typography>
        </DlMuiPopper>
      </div>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{
          sx: {
            minWidth: 60,
            maxWidth: 140,
          },
        }}
      >
        {item.menuItems.map((menuItem) => (
          <React.Fragment key={menuItem.key}>
            {menuItem.key === 'color-normal' ? (
              <ColorMenuItem
                colorValue={normalTextColor || '#4f5e6e'}
                isSelected={menuItem.isActive ? menuItem.isActive(editor) : false}
                onClick={() => {
                  safeExecuteAction(menuItem.action);
                  onClose();
                }}
              />
            ) : menuItem.key.startsWith('color-') && !menuItem.key.includes('divider') ? (
              <ColorMenuItem
                colorValue={menuItem.value}
                isSelected={menuItem.isActive ? menuItem.isActive(editor) : false}
                onClick={() => {
                  safeExecuteAction(menuItem.action);
                  onClose();
                }}
              />
            ) : menuItem.key === 'reset' ? (
              <MenuItem
                onClick={() => {
                  safeExecuteAction(menuItem.action);
                  onClose();
                }}
                sx={{justifyContent: 'center'}}
              >
                <Typography variant='caption' color='text.secondary'>
                  {menuItem.label}
                </Typography>
              </MenuItem>
            ) : menuItem.divider ? (
              <Divider sx={{my: 0.5}} />
            ) : (
              <MenuItem
                onClick={() => {
                  safeExecuteAction(menuItem.action);
                  onClose();
                }}
                selected={menuItem.isActive ? menuItem.isActive(editor) : false}
              >
                {menuItem.icon && <ListItemIcon>{menuItem.icon}</ListItemIcon>}
                <ListItemText primary={menuItem.label} />
              </MenuItem>
            )}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
};

interface EditorToolbarProps {
  editor: Editor;
  disabled: boolean;
  toolbarGroups: ToolbarGroup[];
  canMaximize: boolean;
  maximized: boolean;
  onToggleMaximized: () => void;
  slotProps: any;
  normalTextColor?: string;
  toolbarRef?: React.RefObject<HTMLDivElement>;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  editor,
  disabled,
  toolbarGroups,
  canMaximize,
  maximized,
  onToggleMaximized,
  slotProps,
  normalTextColor,
  toolbarRef,
}) => {
  const theme = useTheme();
  const [openMenus, setOpenMenus] = useState<{[key: string]: HTMLElement | null}>({});

  const safeExecuteAction = useCallback(
    (action?: (editor: Editor) => void) => {
      try {
        if (action && editor && !editor.isDestroyed) {
          action(editor);
        }
      } catch (error) {
        console.warn('Editor action failed:', error);
      }
    },
    [editor]
  );

  const handleMenuOpen = (key: string) => (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenus((prev) => ({...prev, [key]: event.currentTarget}));
  };

  const handleMenuClose = (key: string) => () => {
    setOpenMenus((prev) => ({...prev, [key]: null}));
  };

  return (
    <Toolbar
      variant='dense'
      role='toolbar'
      ref={toolbarRef}
      aria-label='Editor toolbar'
      {...slotProps.Toolbar}
      style={{
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        minHeight: 'auto',
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        ...slotProps.Toolbar?.style,
      }}
    >
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, flex: 1}}>
        {toolbarGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            <ButtonGroup size='small' variant='text' role='group'>
              {group.items
                .filter((item) => item.visible !== false)
                .map((item) => {
                  if (item.type === 'divider') {
                    return <Divider key={item.key} orientation='vertical' flexItem sx={{mx: 1}} role='separator' />;
                  }

                  if (item.type === 'button') {
                    return (
                      <ToolbarButton
                        key={item.key}
                        item={item}
                        editor={editor}
                        disabled={disabled}
                        slotProps={slotProps}
                        safeExecuteAction={safeExecuteAction}
                      />
                    );
                  }

                  if (item.type === 'dropdown') {
                    return (
                      <ToolbarDropdown
                        key={item.key}
                        item={item}
                        editor={editor}
                        disabled={disabled}
                        anchorEl={openMenus[item.key] || null}
                        onOpen={handleMenuOpen(item.key)}
                        onClose={handleMenuClose(item.key)}
                        safeExecuteAction={safeExecuteAction}
                      />
                    );
                  }

                  if (item.type === 'menu') {
                    return (
                      <ToolbarMenu
                        key={item.key}
                        item={item}
                        editor={editor}
                        disabled={disabled}
                        anchorEl={openMenus[item.key] || null}
                        onOpen={handleMenuOpen(item.key)}
                        onClose={handleMenuClose(item.key)}
                        safeExecuteAction={safeExecuteAction}
                        normalTextColor={normalTextColor}
                      />
                    );
                  }

                  return null;
                })}
            </ButtonGroup>
          </React.Fragment>
        ))}
      </Box>

      {canMaximize && (
        <MaximizeButton
          maximized={maximized}
          disabled={disabled}
          toGrayscaleOnDisabled
          toggleMaximized={onToggleMaximized}
          colors={{
            default: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
            hover: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
            selected: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
            selectedHover: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
            active: {background: 'none', fill: 'var(--dlLightIconButtonFill)'},
          }}
        />
      )}
    </Toolbar>
  );
};

interface FormFieldWrapperProps {
  fieldId: string;
  labelId: string;
  translatedLabel?: string;
  required: boolean;
  optional: boolean;
  error: boolean;
  disabled: boolean;
  focused: boolean;
  hasContent: boolean;
  variant: 'outlined' | 'filled' | 'standard';
  fullWidth: boolean;
  children: React.ReactNode;
  slotProps: any;
  toolbarHeight?: number;
}

const FormFieldWrapper: React.FC<FormFieldWrapperProps> = ({
  fieldId,
  labelId,
  translatedLabel,
  required,
  optional,
  error,
  disabled,
  focused,
  hasContent,
  variant,
  fullWidth,
  children,
  slotProps,
  toolbarHeight = 0,
}) => {
  const theme = useTheme();
  const {t} = useFormatters();

  const getLabelPosition = () => {
    if (!focused && !hasContent) {
      const centerPosition = toolbarHeight > 0 ? toolbarHeight + 10 : 50;
      return `translate(14px, ${centerPosition}px) scale(1)`;
    }
    return 'translate(14px, -9px) scale(0.75)';
  };

  return (
    <FormControl
      variant={variant}
      fullWidth={fullWidth}
      error={error}
      disabled={disabled}
      required={required}
      focused={focused}
      data-error={error}
      data-occupied={hasContent}
      data-disabled={disabled}
      data-focused={focused}
      {...slotProps.FormControl}
      style={{
        position: 'relative',
        flex: 1,
        ...slotProps.FormControl?.style,
      }}
    >
      {translatedLabel && (
        <InputLabel
          htmlFor={fieldId}
          id={labelId}
          shrink={focused || hasContent}
          {...slotProps.InputLabel}
          style={{
            transform: getLabelPosition(),
            transition: theme.transitions.create(['color', 'transform'], {
              duration: theme.transitions.duration.shorter,
              easing: theme.transitions.easing.easeOut,
            }),
            transformOrigin: 'top left',
            color: error ? theme.palette.error.main : focused ? theme.palette.primary.main : theme.palette.text.secondary,
            backgroundColor: 'transparent',
            padding: '0 4px',
            pointerEvents: 'none',
            position: 'absolute',
            left: 0,
            top: 0,
            zIndex: focused || hasContent ? 10 : 1,
            ...slotProps.InputLabel?.style,
          }}
        >
          {translatedLabel} {required && !optional && '*'}{' '}
          {optional && <span style={{color: theme.palette.text.secondary}}> {t('global.optional')}</span>}
        </InputLabel>
      )}
      {children}
    </FormControl>
  );
};

interface EditorContentWrapperProps {
  fieldId: string;
  labelId: string;
  translatedLabel?: string;
  error: boolean;
  disabled: boolean;
  focused: boolean;
  hasContent: boolean;
  theme: any;
  minHeight: number;
  maximized: boolean;
  contentStyles: React.CSSProperties;
  editor: Editor;
  toolbar: React.ReactNode;
  slotProps: any;
  optional: boolean;
}

const EditorContentWrapper: React.FC<EditorContentWrapperProps> = ({
  fieldId,
  labelId,
  translatedLabel,
  error,
  disabled,
  focused,
  hasContent,
  theme,
  minHeight,
  maximized,
  contentStyles,
  editor,
  toolbar,
  slotProps,
  optional,
}) => {
  const {t} = useFormatters();
  return (
    <Box
      id={fieldId}
      aria-labelledby={translatedLabel ? labelId : undefined}
      aria-label={!translatedLabel ? 'Rich text editor' : undefined}
      {...slotProps.Content}
      sx={{
        position: 'relative',
        borderRadius: 1,
        ...slotProps.Content?.sx,
      }}
      style={{
        width: '100%',
        height: '100%',
        minHeight: maximized ? '300px' : minHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...slotProps.Content?.style,
      }}
    >
      {/* Fieldset pro outline */}
      <fieldset
        aria-hidden='true'
        style={{
          textAlign: 'left',
          position: 'absolute',
          bottom: 0,
          right: 0,
          top: -5,
          left: 0,
          margin: 0,
          padding: '0 8px',
          pointerEvents: 'none',
          borderRadius: 'inherit',
          borderStyle: 'solid',
          borderWidth: focused ? 2 : 1,
          borderColor: error ? theme.palette.error.main : focused ? theme.palette.primary.main : theme.palette.divider,
          overflow: 'hidden',
          minWidth: '0%',
          ...(disabled && {
            borderColor: theme.palette.action.disabled,
          }),
        }}
      >
        <legend
          style={{
            float: 'unset',
            width: 'auto',
            overflow: 'hidden',
            display: 'block',
            padding: 0,
            height: 11,
            fontSize: '0.75em',
            visibility: 'hidden',
            maxWidth: translatedLabel ? '100%' : '0.01px',
            transition: theme.transitions.create('max-width', {
              duration: theme.transitions.duration.shorter,
            }),
            whiteSpace: 'nowrap',
          }}
        >
          {(focused || hasContent) && (
            <span style={{paddingLeft: 5, paddingRight: 5}}>
              {translatedLabel}
              {translatedLabel && ' '}
              {optional && <span style={{color: theme.palette.text.secondary}}> {t('global.optional')}</span>}
            </span>
          )}
        </legend>
      </fieldset>

      {/* Editor obsah */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          margin: '2px',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <Box sx={{flexShrink: 0}}>{toolbar}</Box>

        <Box
          sx={{
            'display': 'flex',
            'flex': 1,
            'flexDirection': 'column',
            'overflow': 'auto',
            'padding': theme.spacing(2),
            'minHeight': 0,
            '& .ProseMirror': {
              outline: 'none !important',
              border: 'none !important',
              boxShadow: 'none !important',
              minHeight: '100px',
              flex: 1,
            },
            '& .ProseMirror:focus': {
              outline: 'none !important',
              border: 'none !important',
              boxShadow: 'none !important',
            },
            '& .tiptap:focus': {
              outline: 'none !important',
              border: 'none !important',
              boxShadow: 'none !important',
            },
            '& p': {
              marginBottom: '2px',
            },
            '& mark': {
              padding: 0,
            },
            '& ul, & ol, & p': {
              lineHeight: '120%',
            },
            '& ul[data-align="center"], & ol[data-align="center"]': {
              listStylePosition: 'inside',
              textAlign: 'center',
              paddingLeft: 0,
            },
            '& ul[data-align="right"], & ol[data-align="right"]': {
              listStylePosition: 'inside',
              textAlign: 'right',
              paddingLeft: 0,
            },
            '& h3': {
              fontSize: '1.4em',
            },
            '& h4': {
              fontSize: '1.2em',
            },
            '& ul[data-align="center"] p, & ol[data-align="center"] p, & ul[data-align="right"] p, & ol[data-align="right"] p': {
              display: 'inline-block',
            },
            '& .ProseMirror p.is-editor-empty:first-child::before': translatedLabel
              ? {}
              : {
                  color: theme.palette.text.disabled,
                  content: 'attr(data-placeholder)',
                  float: 'left',
                  height: 0,
                  pointerEvents: 'none',
                },
            ...contentStyles,
          }}
        >
          <EditorContent
            className={'tiptapEditor'}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
            editor={editor}
          />
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// TOOLBAR CONFIGURATION
// ============================================================================

const createToolbarConfiguration = (editor: Editor | null, normalTextColor: string, formatters: Formatters): ToolbarGroup[] => {
  const {t} = formatters;
  const normalTextTitle = t('richtext.toolbar.textNormal');
  const headingTitle = t('richtext.toolbar.heading');
  const subHeadingTitle = t('richtext.toolbar.subheading');

  const getCurrentAlignment = (): AlignmentType => {
    if (!editor) return 'left';
    if (editor.isActive({textAlign: 'center'})) return 'center';
    if (editor.isActive({textAlign: 'right'})) return 'right';
    if (editor.isActive({textAlign: 'justify'})) return 'justify';
    return 'left';
  };

  const getCurrentHeadingText = (): string => {
    if (!editor) return 'Nadpis';
    if (editor.isActive('heading', {level: 3})) return headingTitle;
    if (editor.isActive('heading', {level: 4})) return subHeadingTitle;
    return normalTextTitle;
  };

  const getCurrentTextColor = (): string => {
    if (!editor) return normalTextColor || '#4f5e6e';
    const currentColor = editor.getAttributes('textStyle').color;
    return currentColor || normalTextColor || '#4f5e6e';
  };

  const getCurrentHighlightColor = (): string => {
    if (!editor) return '#ffff00';
    const currentColor = editor.getAttributes('highlight').color;
    return currentColor || '#ffff00';
  };

  return [
    {
      items: [
        {
          key: 'heading',
          type: 'dropdown',
          icon: <TitleIcon fontSize='small' />,
          title: t('richtext.toolbar.textFormat'),
          getCurrentValue: getCurrentHeadingText,
          options: [
            {
              key: 'paragraph',
              label: t('richtext.toolbar.textNormal'),
              value: null,
              action: (editor) => editor.chain().focus().setParagraph().run(),
              isActive: (editor) => editor.isActive('paragraph') && !editor.isActive('heading'),
              style: {
                fontSize: '1rem',
                fontWeight: 'normal',
                minHeight: 40,
              },
            },
            {
              key: 'heading2',
              label: t('richtext.toolbar.heading'),
              value: null,
              action: (editor) => editor.chain().focus().toggleHeading({level: 3}).run(),
              isActive: (editor) => editor.isActive('heading', {level: 3}),
              style: {
                fontSize: '1.25rem',
                fontWeight: 'bold',
                minHeight: 40,
              },
            },
            {
              key: 'heading3',
              label: t('richtext.toolbar.subheading'),
              value: null,
              action: (editor) => editor.chain().focus().toggleHeading({level: 4}).run(),
              isActive: (editor) => editor.isActive('heading', {level: 4}),
              style: {
                fontSize: '1.1rem',
                fontWeight: 'bold',
                minHeight: 40,
              },
            },
          ],
        },
      ],
    },
    {
      items: [
        {
          key: 'bold',
          type: 'button',
          icon: <FormatBoldIcon fontSize='small' />,
          title: t('richtext.toolbar.bold'),
          action: (editor) => editor.chain().focus().toggleBold().run(),
          isActive: (editor) => editor.isActive('bold'),
        },
        {
          key: 'italic',
          type: 'button',
          icon: <FormatItalicIcon fontSize='small' />,
          title: t('richtext.toolbar.italics'),
          action: (editor) => editor.chain().focus().toggleItalic().run(),
          isActive: (editor) => editor.isActive('italic'),
        },
        {
          key: 'underline',
          type: 'button',
          icon: <FormatUnderlinedIcon fontSize='small' />,
          title: t('richtext.toolbar.underline'),
          action: (editor) => editor.chain().focus().toggleUnderline().run(),
          isActive: (editor) => editor.isActive('underline'),
        },
        {
          key: 'strike',
          type: 'button',
          icon: <StrikethroughSIcon fontSize='small' />,
          title: t('richtext.toolbar.strikethrough'),
          action: (editor) => editor.chain().focus().toggleStrike().run(),
          isActive: (editor) => editor.isActive('strike'),
        },
        {
          key: 'textAlign',
          type: 'menu',
          icon: (
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              {getAlignmentIcon(getCurrentAlignment())}
              <ArrowDropDownIcon fontSize='small' sx={{ml: 0.5}} />
            </Box>
          ),
          title: t('richtext.toolbar.textAlignment'),
          menuItems: [
            {
              key: 'left',
              label: t('richtext.toolbar.alignLeft'),
              value: 'left',
              action: (editor) => editor.chain().focus().setTextAlign('left').run(),
              isActive: () => getCurrentAlignment() === 'left',
              icon: <FormatAlignLeftIcon fontSize='small' />,
            },
            {
              key: 'center',
              label: t('richtext.toolbar.alignCenter'),
              value: 'center',
              action: (editor) => editor.chain().focus().setTextAlign('center').run(),
              isActive: () => getCurrentAlignment() === 'center',
              icon: <FormatAlignCenterIcon fontSize='small' />,
            },
            {
              key: 'right',
              label: t('richtext.toolbar.alignRight'),
              value: 'right',
              action: (editor) => editor.chain().focus().setTextAlign('right').run(),
              isActive: () => getCurrentAlignment() === 'right',
              icon: <FormatAlignRightIcon fontSize='small' />,
            },
            {
              key: 'justify',
              label: t('richtext.toolbar.alignJustified'),
              value: 'justify',
              action: (editor) => editor.chain().focus().setTextAlign('justify').run(),
              isActive: () => getCurrentAlignment() === 'justify',
              icon: <FormatAlignJustifyIcon fontSize='small' />,
            },
          ],
        },
        {
          key: 'divider1',
          type: 'divider',
        },
      ],
    },
    {
      items: [
        {
          key: 'bulletList',
          type: 'button',
          icon: <FormatListBulletedIcon fontSize='small' />,
          title: t('richtext.toolbar.listIndents'),
          action: (editor) => editor.chain().focus().toggleBulletList().run(),
          isActive: (editor) => editor.isActive('bulletList'),
        },
        {
          key: 'orderedList',
          type: 'button',
          icon: <FormatListNumberedIcon fontSize='small' />,
          title: t('richtext.toolbar.listNumbering'),
          action: (editor) => editor.chain().focus().toggleOrderedList().run(),
          isActive: (editor) => editor.isActive('orderedList'),
        },
        {
          key: 'divider2',
          type: 'divider',
        },
      ],
    },
    {
      items: [
        {
          key: 'textColor',
          type: 'menu',
          icon: (
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <TextFormatIcon fontSize='small' />
              <ArrowDropDownIcon fontSize='small' sx={{ml: 0.5}} />
            </Box>
          ),
          title: t('richtext.toolbar.textColor'),
          menuItems: [
            ...Object.values(TEXT_COLORS).map((colorValue) => ({
              key: `color-${colorValue}`,
              label: colorValue,
              value: colorValue,
              action: (editor: Editor) => editor.chain().focus().setColor(colorValue).run(),
              isActive: () => getCurrentTextColor() === colorValue,
            })),
            {
              key: 'divider-reset',
              label: '',
              value: '',
              divider: true,
            },
            {
              key: 'reset',
              label: 'Reset',
              value: '',
              action: (editor) => editor.chain().focus().unsetColor().run(),
            },
          ],
        },
        {
          key: 'highlight',
          type: 'menu',
          icon: (
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <HighlightIcon style={{fontSize: '1rem', position: 'relative', top: '-2px'}} />
              <ArrowDropDownIcon fontSize='small' sx={{ml: 0.5}} />
            </Box>
          ),
          title: t('richtext.toolbar.textHighlight'),
          menuItems: [
            ...Object.values(HIGHLIGHT_COLORS).map((colorValue) => ({
              key: `color-${colorValue}`,
              label: colorValue,
              value: colorValue,
              action: (editor: Editor) => editor.chain().focus().toggleHighlight({color: colorValue}).run(),
              isActive: () => getCurrentHighlightColor() === colorValue,
            })),
            {
              key: 'divider-highlight-reset',
              label: '',
              value: '',
              divider: true,
            },
            {
              key: 'reset',
              label: 'Reset',
              value: '',
              action: (editor) => editor.chain().focus().unsetHighlight().run(),
            },
          ],
        },
      ],
    },
  ];
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const LightRichTextAreaFilter: React.FC<LightRichTextAreaFilterProps> = ({
  value,
  onChange,
  disabled = false,
  readOnly = false,
  error = false,
  helperText,
  label,
  required = false,
  placeholder = 'Začněte psát…',
  minHeight = 200,
  debounceDelay = 80,
  slots = {},
  slotProps = {},
  toolbarGroups,
  contentStyles = {},
  variant = 'outlined',
  fullWidth = false,
  optional = false,
  canMaximize = false,
  config,
  isDefaultValue,
  normalTextColor,
  onGetCurrentValue,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  const [maximized, setMaximized] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hasContent, setHasContent] = useState(() => {
    if (isDefaultValue !== undefined) return !isDefaultValue;
    if (value) return !isContentEmpty(value);
    return false;
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [fieldId] = useState(() => `mui-${Math.random().toString(36).substring(2, 11)}`);
  const labelId = `${fieldId}-label`;

  const translatedLabel = config?.ui?.label ? TranslateUtils.translate(config.ui.label, intl) : label;
  const translatedPlaceholder = config?.ui?.placeholder ? TranslateUtils.translate(config.ui.placeholder, intl) : placeholder;

  const debouncedOnChange = useDebounce(onChange, debounceDelay);

  const extensions = useMemo(
    () => [
      StarterKit.configure({codeBlock: false}),
      TextStyle,
      Color,
      Highlight.configure({multicolor: true}),
      Underline,
      Strike,
      HorizontalRule,
      HardBreak.configure({keepMarks: false}),
      TextAlignWithLists.configure({
        types: ['heading', 'paragraph', 'bulletList', 'orderedList'],
        alignments: ['left', 'center', 'right', 'justify'],
        defaultAlignment: 'left',
      }),
      ...(translatedLabel
        ? []
        : [
            Placeholder.configure({
              placeholder: translatedPlaceholder || '',
              showOnlyWhenEditable: true,
              showOnlyCurrent: false,
            }),
          ]),
    ],
    [translatedPlaceholder, translatedLabel]
  );

  const prevValueRef = useRef(value);
  const isUpdatingFromEditor = useRef(false);
  const prevDisabledRef = useRef(disabled);
  const contentRef = useRef(value);

  const skipNextOnChange = useRef(false);

  const editor = useEditor({
    extensions,
    content: value || undefined,
    editable: !disabled && !readOnly,
    onUpdate: ({editor}) => {
      const json = editor.getJSON();
      const html = editor.getHTML();
      const text = editor.getText();

      const isEmpty = isContentEmpty(json);
      const hasTextContent = !isEmpty;

      const prevContent = contentRef.current;
      const isFirstRender = prevContent === null;

      if (jsonContentEquals(json, prevContent)) {
        console.log('⏭️ Skipping onUpdate - content unchanged');
        return;
      }

      if (!isInitialized) {
        console.log('⏭️ Skipping onUpdate - reinitializing');
        return;
      }

      setHasContent(hasTextContent);
      contentRef.current = json;

      isUpdatingFromEditor.current = true;

      if (!skipNextOnChange.current && !isFirstRender) {
        debouncedOnChange(json, html, text);
      } else {
        skipNextOnChange.current = false; // Reset flag
      }
    },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    onCreate: ({editor}) => {
      setIsInitialized(true);
      const json = editor.getJSON();
      const isEmpty = isContentEmpty(json);
      setHasContent(!isEmpty);
      contentRef.current = json;
    },
  });

  useEffect(() => {
    if (!editor || !isInitialized) return;

    if (isUpdatingFromEditor.current) {
      isUpdatingFromEditor.current = false;
      prevValueRef.current = value;
      prevDisabledRef.current = disabled;
      return;
    }

    const valueChanged = value !== prevValueRef.current;
    const disabledChanged = disabled !== prevDisabledRef.current;

    if (valueChanged && disabledChanged) {
      console.log('Skipping onChange - simultaneous disabled and value change');
      skipNextOnChange.current = true;
    }

    if (valueChanged) {
      try {
        const currentContent = editor.getJSON();
        if (!jsonContentEquals(currentContent, value)) {
          editor.commands.setContent(value || '', false, {
            preserveWhitespace: 'full',
          });
          contentRef.current = value;
        }
      } catch (error) {
        console.warn('Failed to update editor content:', error);
      }
    }

    prevValueRef.current = value;
    prevDisabledRef.current = disabled;
  }, [editor, value, disabled, isInitialized]);

  useEffect(() => {
    if (onGetCurrentValue && editor && isInitialized) {
      onGetCurrentValue(() => {
        if (!editor || editor.isDestroyed) {
          return {
            json: value || {},
            html: '',
            text: '',
          };
        }
        const json = editor.getJSON();
        const html = editor.getHTML();
        const text = editor.getText();
        return {json, html, text};
      });
    }
  }, [editor, isInitialized, onGetCurrentValue, value]);

  useEffect(() => {
    if (!editor || !isInitialized) return;

    const wasDisabled = prevDisabledRef.current;
    const isNowDisabled = disabled || readOnly;

    if (wasDisabled && !isNowDisabled) {
      try {
        const currentContent = contentRef.current || value;
        if (currentContent) {
          editor.commands.setContent(currentContent, false, {
            preserveWhitespace: 'full',
          });
        }
      } catch (error) {
        console.warn('Failed to restore content with formatting:', error);
      }
    }

    editor.setEditable(!isNowDisabled);
    prevDisabledRef.current = isNowDisabled;
  }, [editor, disabled, readOnly, isInitialized, value]);

  const formatters = useFormatters();

  const defaultToolbarGroups = useMemo(
    () => createToolbarConfiguration(editor, normalTextColor || '#4f5e6e', formatters),
    [editor, formatters, normalTextColor]
  );

  const finalToolbarGroups = useMemo(() => {
    const sourceGroups = toolbarGroups || defaultToolbarGroups;
    return sourceGroups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => item.visible !== false),
      }))
      .filter((group) => group.items.length > 0);
  }, [toolbarGroups, defaultToolbarGroups]);

  const [toolbarHeight, setToolbarHeight] = useState(0);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const measureToolbarHeight = useCallback(() => {
    if (toolbarRef.current) {
      const {height} = toolbarRef.current.getBoundingClientRect();
      console.info('Toolbar height:', height);
      setToolbarHeight(height);
    }
  }, []);
  useLayoutEffect(() => {
    measureToolbarHeight();
    if (toolbarRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        measureToolbarHeight();
      });
      resizeObserver.observe(toolbarRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    return () => {};
  }, [measureToolbarHeight]);

  useLayoutEffect(() => {
    const timeoutId = setTimeout(() => {
      measureToolbarHeight();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [finalToolbarGroups, maximized, measureToolbarHeight]);

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => {
        measureToolbarHeight();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [measureToolbarHeight]);

  const Root = slots.Root || Box;

  const handleFocusIn = useCallback(
    (e: FocusEvent) => {
      if (!focused) setFocused(true);
    },
    [focused]
  );

  const handleFocusOut = useCallback((e: FocusEvent) => {
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setFocused(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (editor && !editor.isDestroyed) {
        try {
          editor.destroy();
        } catch (error) {
          console.warn('Failed to destroy editor:', error);
        }
      }
    };
  }, [editor]);

  if (!editor || !isInitialized) {
    return null;
  }

  const toolbar = disabled ? null : (
    <EditorToolbar
      editor={editor}
      disabled={disabled}
      toolbarGroups={finalToolbarGroups}
      canMaximize={canMaximize}
      maximized={maximized}
      onToggleMaximized={() => {
        setMaximized(!maximized);
      }}
      slotProps={slotProps}
      normalTextColor={normalTextColor}
      toolbarRef={toolbarRef}
    />
  );

  return (
    <Root
      data-name={config?.name}
      onFocusCapture={handleFocusIn}
      onBlurCapture={handleFocusOut}
      data-input-can-maximize={canMaximize}
      data-input-maximized={maximized}
      className={classNames(lightFilterNodeStyles.dlFilterNodeContent, 'dlFilterNode')}
      {...slotProps.Root}
      style={{
        display: 'flex',
        maxWidth: fullWidth ? '100%' : '362px',
        padding: '4px 0',
        paddingBottom: helperText ? '19px' : '4px',
        width: fullWidth ? '100%' : undefined,
        ...slotProps.Root?.style,
      }}
    >
      <FormFieldWrapper
        fieldId={fieldId}
        labelId={labelId}
        translatedLabel={translatedLabel}
        required={required}
        optional={optional}
        error={error}
        disabled={disabled}
        focused={focused}
        hasContent={hasContent}
        variant={variant}
        fullWidth={fullWidth}
        slotProps={slotProps}
        toolbarHeight={toolbarHeight}
      >
        <EditorContentWrapper
          fieldId={fieldId}
          labelId={labelId}
          translatedLabel={translatedLabel}
          error={error}
          disabled={disabled}
          focused={focused}
          hasContent={hasContent}
          theme={theme}
          minHeight={minHeight}
          maximized={maximized}
          contentStyles={contentStyles}
          editor={editor}
          toolbar={toolbar}
          slotProps={slotProps}
          optional={optional}
        />
        {helperText && (
          <FormHelperText
            error={error}
            role='status'
            aria-live='polite'
            {...slotProps.HelperText}
            style={{position: 'absolute', bottom: '-15px', ...slotProps.HelperText?.style}}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormFieldWrapper>
    </Root>
  );
};

export default LightRichTextAreaFilter;
